import SystemError from 'components/SystemError';
import { useFormik } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button,
  designTokens,
  Flex,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { useForgotPassword } from '../../hooks/useForgotPassword';
import EmailSuccess from './EmailRequestSuccess';
import { ForgotPasswordValidationSchema } from './ForgotPasswordValidationSchema';
import TokenExpired from './TokenExpired';

const ForgotPassword = (): JSX.Element => {
  const { t } = useTranslation();

  const [forgotPasswordDataState, forgotPasswordDataHandler] =
    useForgotPassword();

  const isEmailInstructionsSent = forgotPasswordDataState?.data !== undefined;

  const history = useHistory<{ username: string; isTokenExpired?: boolean }>();

  const username = history?.location?.state?.username || '';
  const isTokenExpired = history?.location?.state.isTokenExpired;
  const showTokenExpiredMessage =
    isTokenExpired &&
    !forgotPasswordDataState?.error &&
    !isEmailInstructionsSent;

  const formik = useFormik({
    initialValues: {
      username: username,
    },
    validationSchema: ForgotPasswordValidationSchema,
    validateOnChange: false,
    onSubmit: () => {
      forgotPasswordDataHandler({
        username: formik.values.username,
      });
    },
  });
  return (
    <>
      {forgotPasswordDataState?.error && (
        <SystemError statusCode={forgotPasswordDataState?.error?.statusCode} />
      )}
      {showTokenExpiredMessage && <TokenExpired />}
      <Typography
        variant="title"
        css={{ marginBottom: designTokens.spaceMedium }}
      >
        {t('forgotPasswordPage.resetPasswordPageHeader')}
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        {!isEmailInstructionsSent ? (
          <TextField
            data-testid="email-input"
            css={{ marginBottom: designTokens.spaceXSmall }}
            helperText={formik.errors.username}
            placeholder={t('forgotPasswordPage.textField.placeholder')}
            label={t('forgotPasswordPage.textField.label')}
            value={formik.values.username}
            isDisabled={forgotPasswordDataState?.isLoading}
            isRequired={true}
            validationState={formik.errors.username ? 'invalid' : 'valid'}
            onChange={(currentValue) => {
              formik.errors.username && formik.setErrors({ username: '' });
              formik.setFieldValue('username', currentValue);
            }}
          />
        ) : (
          <EmailSuccess username={formik.values.username} />
        )}

        <Flex gap="small" css={{ marginTop: designTokens.spaceMedium }}>
          <Button
            css={isEmailInstructionsSent ? { width: '100%' } : {}}
            variant="secondary"
            isDisabled={forgotPasswordDataState?.isLoading}
            onClick={() => {
              history.push('/login');
            }}
          >
            Back to login
          </Button>
          {!isEmailInstructionsSent && (
            <Button
              css={{ width: '100%' }}
              type="submit"
              variant="brand"
              isDisabled={forgotPasswordDataState?.isLoading}
            >
              {forgotPasswordDataState?.isLoading
                ? t('forgotPasswordPage.sendingResetInstructions')
                : t('forgotPasswordPage.sendResetInstructions')}
            </Button>
          )}
        </Flex>
      </form>
    </>
  );
};

export default ForgotPassword;
